<template>
  <div class="container" style="margin-top: 90px; padding: 0 20px;">
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <h2 class="subtitle">
      {{ $t("productCombo.title") }}
      &nbsp;&nbsp;
      <button
        class="button is-small"
        @click="createEditCombo()"
        style="position: relative; bottom: 2px;"
      >
        <b-icon icon="plus-circle" type="is-primary"></b-icon>
      </button>
    </h2>

    <p v-if="!productCombos.length">
      {{ $t("productCombo.noCombosAtThisMoment") }}
    </p>

    <b-table
      class="sticky-table-header"
      v-else
      :data="productCombos"
      hoverable
      :key="tableKey"
    >
      <b-table-column :label="$t('productCombo.product')" v-slot="props">
        {{ props.row.product ? props.row.product.n : "-" }}
      </b-table-column>

      <b-table-column :label="$t('productCombo.stores')" v-slot="props">
        {{ storeNames(props.row) }}
      </b-table-column>

      <b-table-column :label="$t('productCombo.buyQty')" v-slot="props">
        {{ props.row.buyQty }}
      </b-table-column>

      <b-table-column :label="$t('productCombo.takeQty')" v-slot="props">
        {{ props.row.takeQty }}
      </b-table-column>

      <b-table-column :label="$t('productCombo.startDate')" v-slot="props">
        {{
          moment
            .utc(props.row.startDate)
            .add(preferences.timezone, "h")
            .format("DD/MM/YY HH:mm")
        }}
      </b-table-column>

      <b-table-column :label="$t('productCombo.endDate')" v-slot="props">
        {{
          moment
            .utc(props.row.endDate)
            .add(preferences.timezone, "h")
            .format("DD/MM/YY HH:mm")
        }}
      </b-table-column>

      <b-table-column :label="$t('common.status')" v-slot="props">
        <b-switch
          size="is-small"
          type="is-primary"
          passive-type="is-secondary"
          v-model="props.row.active"
          @input="turnOnOffCombo(props.row)"
        ></b-switch>

        {{ props.row.active ? $t("common.Active") : $t("common.Inactive") }}
      </b-table-column>

      <b-table-column :label="$t('common.edit')" v-slot="props">
        <button class="button is-small" @click="createEditCombo(props.row)">
          <b-icon icon="edit" type="is-primary"></b-icon>
        </button>
      </b-table-column>

      <b-table-column :label="$t('common.delete')" v-slot="props">
        <button
          class="button is-small"
          @click="confirmComboDeletion(props.row)"
        >
          <b-icon icon="minus-circle" type="is-primary"></b-icon>
        </button>
      </b-table-column>

      <template slot="empty">
        <table-empty-template />
      </template>
    </b-table>

    <b-modal :active.sync="isModalActive">
      <new-product-combo-modal
        :currentCombo="currentCombo"
        @increment-key="tableKey++"
      />
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../mixins/common";
import newProductComboModal from "./NewProductComboModal";
import tableEmptyTemplate from "../common/TableEmptyTemplate";

export default {
  mixins: [common],

  data() {
    return {
      isLoading: false,
      isModalActive: false,
      tableKey: 0,
      currentCombo: null
    };
  },

  components: {
    newProductComboModal,
    tableEmptyTemplate
  },

  computed: {
    ...mapGetters(["productCombos", "preferences"])
  },

  created() {
    this.isLoading = true;
    this.$store
      .dispatch("getProductCombos")
      .then(res => {
        if (res.data.result === "error") this.alertError(res.data);
      })
      .catch(() => {
        this.alertError({ message: "UNEXPECTED_ERROR" });
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    storeNames(combo) {
      return Array.isArray(combo.stores)
        ? combo.stores.map(s => s.storeInfo.name).join(", ")
        : "-";
    },

    createEditCombo(combo) {
      if (combo) {
        this.currentCombo = JSON.parse(JSON.stringify(combo));
        this.currentCombo.startDate = new Date(combo.startDate);
        this.currentCombo.endDate = new Date(combo.endDate);
      } else {
        this.currentCombo = null;
      }
      this.isModalActive = true;
    },

    confirmComboDeletion(combo) {
      this.$buefy.dialog.confirm({
        message: `${this.$t("productCombo.confirmComboDeletion")} ${
          combo.product ? combo.product.n : ""
        }`,
        onConfirm: () => this.deleteCombo(combo)
      });
    },

    deleteCombo(combo) {
      this.isLoading = true;
      this.$store
        .dispatch("deleteProductCombo", { comboId: combo._id })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$buefy.toast.open({
              duration: 3000,
              message: this.$t("productCombo.comboDeleted"),
              position: "is-bottom",
              type: "is-primary"
            });
            this.tableKey++;
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    turnOnOffCombo(combo) {
      this.isLoading = true;
      this.$store
        .dispatch("updateProductCombo", {
          comboId: combo._id,
          stores: combo.stores.map(s => s._id),
          productId: combo.product._id,
          buyQty: combo.buyQty,
          takeQty: combo.takeQty,
          startDate: combo.startDate,
          endDate: combo.endDate,
          active: combo.active
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
            combo.active = !combo.active;
          } else {
            this.$buefy.toast.open({
              duration: 3000,
              message: combo.active
                ? this.$t("common.Active")
                : this.$t("common.Inactive"),
              position: "is-bottom",
              type: "is-primary"
            });
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
          combo.active = !combo.active;
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
};
</script>
